import React, {
  createContext,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useCompleteSignIn, useSignOut, useSessionCookie } from '#hooks/auth';
import { getStorage } from '#utils/storage';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const storage = getStorage();
  const [searchParams] = useSearchParams();
  const { sessionCookie, removeSessionCookie } = useSessionCookie();

  const [isAuthenticated, setIsAuthenticated] = useState(!!sessionCookie);

  const handleSignInCompleted = useCallback(() => {
    navigate('/settings/organizations', { replace: true });
    setIsAuthenticated(true);
  }, [navigate]);

  const handleSignOutFinally = useCallback(() => {
    removeSessionCookie();
    setIsAuthenticated(false);
    storage.clear();
  }, [storage, removeSessionCookie]);

  const { trigger: triggerSignIn, loading: isSigningIn } = useCompleteSignIn({
    onCompleted: handleSignInCompleted,
  });
  const { trigger: signOut, loading: isSigningOut } = useSignOut({
    onFinally: handleSignOutFinally,
  });

  const signIn = useCallback(() => {
    const code = searchParams.get('code');
    if (code) triggerSignIn(code);
  }, [searchParams, triggerSignIn]);

  const contextValue = useMemo(() => ({
    isAuthenticated,
    loading: isSigningIn || isSigningOut,
    signIn,
    signOut,
  }), [isAuthenticated, isSigningIn, isSigningOut, signIn, signOut]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
